import Phaser from 'phaser';
import type {
  Player,
  Territory,
  TerritoryId,
  TerritoryState,
} from '../../game/types.js';
import { MAP_Y_OFFSET } from '../PhaserGame.js';
import {
  COLORS,
  FONT_HEADER,
  FONT_MONO,
  TERRITORY_SELECTED_STROKE_WIDTH,
  TERRITORY_SIZE as DEFAULT_TERRITORY_SIZE,
  TERRITORY_STROKE_WIDTH,
} from '../style.js';

export type TerritoryTapHandler = (id: TerritoryId) => void;

export type TerritorySelectionState =
  | 'none'
  | 'selected' // action source (own territory) — bright lime ring
  | 'target' // hostile destination (attack) — red ring
  | 'destination'; // friendly destination (fortify) — cyan ring

const SHADOW_OFFSET_Y = 4;
const SHADOW_ALPHA = 0.45;

// Phaser depth for a selected/target/destination tile. All four of the
// tile's elements (shadow, square, unit label, compass label) are bumped
// to this depth when the tile becomes active so the wider stroke ring
// is never covered by neighboring tiles drawn later in the territories
// array. Chrome (HUD/tray/footer/nav) lives in different y-zones from
// the map so a depth-100 tile doesn't visually conflict with anything.
const SELECTED_DEPTH = 100;

/**
 * Renders one territory tile: drop-shadowed square, filled with player
 * color, steel outline, compass abbreviation at the top, mono unit count
 * centered. Squares within a region sit flush so they read as a single
 * continent — territory names live as compass abbreviations inside the
 * tiles since putting labels below would overlap the next tile.
 */
export class TerritoryView {
  private readonly scene: Phaser.Scene;
  private readonly shadow: Phaser.GameObjects.Rectangle;
  private readonly square: Phaser.GameObjects.Rectangle;
  private readonly unitLabel: Phaser.GameObjects.Text;
  private readonly compassLabel: Phaser.GameObjects.Text;
  // Last unit count we rendered, so we can pop the label only when the
  // value actually changed (initial setup shouldn't pop). null until
  // first update.
  private lastUnits: number | null = null;

  constructor(
    scene: Phaser.Scene,
    territory: Territory,
    onTap: TerritoryTapHandler,
    tileSize: number = DEFAULT_TERRITORY_SIZE
  ) {
    this.scene = scene;
    // Apply the presentation-layer map offset so legacy map data
    // (authored against the pre-shrink HUD layout) renders centered
    // in the current map zone. See PhaserGame.ts MAP_Y_OFFSET.
    const x = territory.position.x;
    const y = territory.position.y + MAP_Y_OFFSET;
    // Scale labels with the tile size so the same proportions render
    // on Ring (80px) and Terra (64px). Tied to a 80px reference; under
    // that, fonts shrink linearly.
    const sizeScale = tileSize / DEFAULT_TERRITORY_SIZE;
    const compassPx = Math.max(9, Math.round(11 * sizeScale));
    const unitPx = Math.max(18, Math.round(26 * sizeScale));

    // Drop shadow — same-size square offset down a few pixels.
    this.shadow = scene.add.rectangle(
      x,
      y + SHADOW_OFFSET_Y,
      tileSize,
      tileSize,
      0x000000,
      SHADOW_ALPHA
    );
    this.shadow.setBlendMode(Phaser.BlendModes.MULTIPLY);

    this.square = scene.add.rectangle(
      x,
      y,
      tileSize,
      tileSize,
      COLORS.bgDeepInt
    );
    this.square.setStrokeStyle(
      TERRITORY_STROKE_WIDTH,
      COLORS.nodeStrokeInt
    );
    this.square.setInteractive({ cursor: 'pointer' });
    this.square.on('pointerdown', () => onTap(territory.id));

    // Compass abbreviation at the top of the tile (e.g. "NW", "MID", "S").
    const abbrev = compassAbbrev(territory.id);
    this.compassLabel = scene.add
      .text(x, y - tileSize / 2 + Math.round(12 * sizeScale), abbrev, {
        fontFamily: FONT_HEADER,
        fontSize: `${compassPx}px`,
        color: COLORS.textSecondary,
        fontStyle: '600',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Unit count — slightly below center for visual balance under the
    // abbreviation.
    this.unitLabel = scene.add
      .text(x, y + Math.round(8 * sizeScale), '', {
        fontFamily: FONT_MONO,
        fontSize: `${unitPx}px`,
        color: COLORS.textOnNode,
        fontStyle: '700',
      })
      .setOrigin(0.5);
  }

  update(state: TerritoryState, owner: Player): void {
    const fill = Phaser.Display.Color.HexStringToColor(owner.color).color;
    this.square.setFillStyle(fill);
    this.unitLabel.setText(String(state.units));

    // Pop the label whenever the count actually changes — feedback that
    // a deploy / attack / advance / fortify moved units in or out. First
    // update (lastUnits === null) is the initial layout; no pop.
    if (this.lastUnits !== null && this.lastUnits !== state.units) {
      this.scene.tweens.killTweensOf(this.unitLabel);
      this.unitLabel.setScale(1);
      this.scene.tweens.add({
        targets: this.unitLabel,
        scale: { from: 1.6, to: 1 },
        duration: 350,
        ease: 'Back.easeOut',
      });
    }
    this.lastUnits = state.units;
  }

  /**
   * Spawn a floating "+N" gain number above the unit count, drift up
   * and fade out, then destroy. Mirror of showLossFloater for the
   * positive case — called immediately on deploy / advance / arriving
   * fortify so the player gets local feedback before the server
   * round-trip completes. The actual count update lands later via
   * the standard `update()` path (with its own scale-pop on the
   * unit label).
   */
  showGainFloater(amount: number, hexColor?: string): void {
    if (amount <= 0) return;
    const startX = this.unitLabel.x;
    const startY = this.unitLabel.y - 6;
    const text = this.scene.add
      .text(startX, startY, `+${amount}`, {
        fontFamily: FONT_MONO,
        fontSize: '22px',
        color: hexColor ?? COLORS.accentCyanHex,
        fontStyle: '700',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(170);

    this.scene.tweens.add({
      targets: text,
      y: startY - 48,
      alpha: { from: 1, to: 0 },
      duration: 800,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  /**
   * Brief color flash over the tile signaling conquest — the attacker's
   * color sweeps in, peaks, fades. Fires from the combatResolved
   * handler when conquered=true. Pure VFX; the real fill change still
   * happens through `update()` driven by the new state.
   */
  flashConquest(colorInt: number): void {
    const overlay = this.scene.add
      .rectangle(
        this.square.x,
        this.square.y,
        this.square.width,
        this.square.height,
        colorInt
      )
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(SELECTED_DEPTH + 5);

    // Quick fade in, slower fade out — peak alpha ~600ms in feels
    // like the moment the tile changes hands.
    this.scene.tweens.add({
      targets: overlay,
      alpha: { from: 0, to: 0.75 },
      duration: 220,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: overlay,
          alpha: { from: 0.75, to: 0 },
          duration: 480,
          ease: 'Cubic.easeIn',
          onComplete: () => overlay.destroy(),
        });
      },
    });
  }

  /**
   * Spawn a floating "−N" damage number above the unit count, drift up
   * and fade out, then destroy. Called by GameScene from combat
   * results — losses come straight from the combat log so the number
   * is exactly what the rules said it should be.
   */
  showLossFloater(amount: number): void {
    if (amount <= 0) return;
    const startX = this.unitLabel.x;
    const startY = this.unitLabel.y - 6;
    const text = this.scene.add
      .text(startX, startY, `-${amount}`, {
        fontFamily: FONT_MONO,
        fontSize: '20px',
        color: COLORS.accentRedHex,
        fontStyle: '700',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(170);

    this.scene.tweens.add({
      targets: text,
      y: startY - 44,
      alpha: { from: 1, to: 0 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  setSelection(state: TerritorySelectionState): void {
    // Bring all four elements to the front when the tile is active so the
    // wide ring isn't covered at the seam by a neighboring tile drawn
    // later in the territories array. Within depth 100 the elements
    // retain their creation order: shadow → square → labels.
    const depth = state === 'none' ? 0 : SELECTED_DEPTH;
    this.shadow.setDepth(depth);
    this.square.setDepth(depth);
    this.unitLabel.setDepth(depth);
    this.compassLabel.setDepth(depth);

    switch (state) {
      case 'none':
        this.square.setStrokeStyle(
          TERRITORY_STROKE_WIDTH,
          COLORS.nodeStrokeInt
        );
        return;
      case 'selected':
        this.square.setStrokeStyle(
          TERRITORY_SELECTED_STROKE_WIDTH,
          COLORS.selectedStrokeInt
        );
        return;
      case 'target':
        this.square.setStrokeStyle(
          TERRITORY_SELECTED_STROKE_WIDTH,
          COLORS.attackTargetStrokeInt
        );
        return;
      case 'destination':
        this.square.setStrokeStyle(
          TERRITORY_SELECTED_STROKE_WIDTH,
          COLORS.accentCyanInt
        );
        return;
    }
  }
}

function compassAbbrev(id: TerritoryId): string {
  const parts = String(id).split('_');
  const tail = parts[parts.length - 1] ?? '';
  return tail.toUpperCase();
}
